// @flow

import { useEffect, useCallback, useRef } from 'react';
import * as WidgetPropsModel from '../../../models/widget-props';
import WidgetUI from '../../widgets-ui/col-heatmap';
import { buildWidgetDateString } from '../common/stringHelpers';
import { useHasWidgetChanged, useSafeState, useSafeVisible } from '../common/hooks';
import Pagination from '../../pagination';
import { IS_EMAIL_VIEW } from '../../../config/vars';
import { formatNumbersWithUnits } from '../../../utils/formatNumbers';
import { processMetricHeatmapWidget } from '@auravisionlabs/aura-pkg-report-widgets/dist/core/widgets/metric-heatmap';
import { browserQueryAPI } from '../../../services/query-api-browser';
import * as AvailableMetricsModel from '../../../models/available-metrics';
import { useWidgetRenderState } from '../../../hooks/use-widget-render-state';

const MetricHeatmapWidget = ({
  widget,
  availableMetrics,
  locations = [],
  uiOptions = {},
  reportWidgetId,
}: WidgetPropsModel.metricHeatmapT) => {
  const [loading, setLoading] = useSafeState(false);
  const [progress, setProgress] = useSafeState(0);
  const [error, setError] = useSafeState(false);
  const [autoTitle, setAutoTitle] = useSafeState('');
  const [tableRows, setTableRows] = useSafeState([]);
  const [tableColumns, setTableColumns] = useSafeState([]);
  const hasWidgetChanged = useHasWidgetChanged(widget);
  const [fetchRequired, setFetchRequired] = useSafeState(true); // Start true for initial fetch
  const [currentElement, isVisible] = useSafeVisible(
    (vi) => uiOptions.disableViewportLoading || vi > 0
  );
  const [visibleLocations, setVisibleLocations] = useSafeState([]);
  const [page, setPage] = useSafeState(0);
  const [itemsPerPage, setItemsPerPage] = useSafeState(
    IS_EMAIL_VIEW ? -1 : uiOptions.itemsPerPage || -1
  );
  const { reportLoading, reportComplete, reportError } = useWidgetRenderState(reportWidgetId);
  
  // Track if initial fetch has completed to avoid re-triggering on mount
  const hasInitialFetchedRef = useRef(false);

  const loadWidgetData = useCallback(async () => {
    setLoading(true);
    setError(false);
    setProgress(0);
    reportLoading();

    try {
      // Build config for the package
      const config = {
        metrics: widget.metrics,
        locations: visibleLocations,
        period: widget.period,
        comparisonPeriod: widget.comparisonPeriod,
        demographicFilter: widget.demographicFilter,
        showChangeValue: widget.showChangeValue,
        breakdownBy: widget.breakdownBy,
        breakdownTaxonomies: widget.breakdownTaxonomies,
        breakdownFilters: widget.breakdownFilters,
        ...(widget.customTitle && { customTitle: widget.customTitle }),
      };

      const deps = {
        queryAPI: browserQueryAPI,
        availableMetrics: availableMetrics,
        locations: locations,
        startOfWeek: 'monday',
        onProgress: (completed, total) => setProgress(completed / total),
      };

      const widgetData = await processMetricHeatmapWidget(config, deps);

      // Use title from package
      setAutoTitle(widgetData.title);

      // Extract table rows (first column is breakdown)
      const rows = widgetData.rows.map((row) => ({
        label: row[0].value,
        link: undefined, // We don't have link information from package
      }));
      setTableRows(rows);

      // Extract table columns (skip first column which is breakdown)
      const cols = widgetData.columns.slice(1).map((col, colIndex) => {
        // Get metric details for description
        const metricSpec = widget.metrics[colIndex];
        const metricDetails = metricSpec
          ? AvailableMetricsModel.findMetricDetails(
              availableMetrics,
              metricSpec.metricGroupKey,
              metricSpec.metricKey
            )
          : null;

        const desc = metricDetails
          ? AvailableMetricsModel.getMetricDescription(metricDetails)
          : '';

        const demos = metricSpec && metricSpec.demographicFilter
          ? Object.values(metricSpec.demographicFilter).filter(Boolean).join(" ")
          : '';

        return {
          label: col.header,
          description: uiOptions.disableDescription
            ? ''
            : demos.length
              ? `${desc} (${demos})`
              : desc,
          unitType: col.defaultUnit,
          rows: widgetData.rows.map((row) => {
            const cell = row[colIndex + 1];
            return {
              value: cell.value,
              changeValue: widget.showChangeValue ? cell.value : null,
              unitType: cell.unit,
            };
          }),
        };
      });
      setTableColumns(cols);

      // Report completion to context - use package output directly
      reportComplete(widgetData);

    } catch (err) {
      console.error('Error loading Metric Heatmap widget:', err);
      setError(true);
      reportError(err.message || 'Failed to load Metric Heatmap widget');
    } finally {
      setLoading(false);
    }
  }, [
    widget,
    availableMetrics,
    locations,
    visibleLocations,
    uiOptions.disableDescription,
  ]);

  useEffect(() => {
    if (hasWidgetChanged && hasInitialFetchedRef.current) setFetchRequired(true);
  }, [hasWidgetChanged]);

  useEffect(() => {
    if (fetchRequired && isVisible) {
      setFetchRequired(false);
      hasInitialFetchedRef.current = true;
      loadWidgetData();
    }
  }, [fetchRequired, isVisible, loadWidgetData]);

  useEffect(() => {
    const start = page * Math.max(0, itemsPerPage);
    const end =
      itemsPerPage > 0
        ? Math.min(start + itemsPerPage, widget.locations.length)
        : widget.locations.length;
    setVisibleLocations(widget.locations.slice(start, end));
  }, [page, widget.locations, itemsPerPage]);

  // Track previous visibleLocations to trigger re-fetch only when pagination changes
  const prevVisibleLocationsRef = useRef<string>('');
  useEffect(() => {
    const currentKey = visibleLocations.map(l => l.id || l).join(',');
    if (currentKey !== prevVisibleLocationsRef.current) {
      const wasEmpty = prevVisibleLocationsRef.current === '';
      prevVisibleLocationsRef.current = currentKey;
      // Only set fetchRequired if this is a pagination change (not initial mount)
      if (currentKey && !wasEmpty && hasInitialFetchedRef.current) {
        setFetchRequired(true);
      }
    }
  }, [visibleLocations]);

  // Go back to the first page on changing page length
  useEffect(() => {
    setPage(0);
  }, [itemsPerPage, setPage]);

  const title = widget.customTitle || autoTitle;
  const subtitle = buildWidgetDateString(widget, true);

  return (
    <div ref={currentElement}>
      <WidgetUI
        errors={error}
        loading={loading}
        progress={progress}
        title={title}
        subtitle={subtitle}
        tableColumns={tableColumns}
        tableRows={tableRows}
        colorScheme={widget.colorScheme}
        dataKey={widget.showChangeValue ? 'changeValue' : 'value'}
        sortByColumn={widget.sortByColumn}
        sortDirection={widget.sortDirection}
      >
        <Pagination
          pages={locations.length / itemsPerPage}
          setPage={setPage}
          currentPage={page}
          setItemsPerPage={setItemsPerPage}
          itemsPerPage={itemsPerPage}
        />
      </WidgetUI>
    </div>
  );
};

export default MetricHeatmapWidget;
